import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserPayload } from '../features/users/types';
import { RequiredFieldsLegend } from './RequiredFieldsLegend';

const createSchema = z.object({
  email: z.string().email('Email inválido').min(1, 'Requerido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
  role: z.string().min(1, 'Selecciona un rol')
});

const editSchema = z.object({
  email: z.string().email('Email inválido').min(1, 'Requerido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres').optional().or(z.literal('')),
  role: z.string().min(1, 'Selecciona un rol')
});

type FormValues = z.infer<typeof editSchema>;

interface Props {
  initialValues?: UserPayload | null;
  onSubmit: (values: FormValues) => void;
  onCancel?: () => void;
  error?: string;
  onEmailChange?: (value: string) => void;
  roleOptions?: string[]; // Roles organizacionales del ABM + 'superadmin'
}

export function UsersForm({ initialValues, onSubmit, onCancel, error, onEmailChange, roleOptions = [] }: Props) {
  const isEditing = !!initialValues;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: zodResolver(isEditing ? editSchema : createSchema) as Resolver<FormValues>,
    defaultValues: initialValues ?? { email: '', password: '', role: '' }
  });

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    } else {
      reset({ email: '', password: '', role: '' });
    }
  }, [initialValues, reset]);

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
          type="email"
          placeholder="usuario@empresa.com"
          {...register('email')}
          onChange={(e) => {
            register('email').onChange(e);
            onEmailChange?.(e.target.value);
          }}
        />
        {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        {!errors.email && error && <p className="text-sm text-destructive">{error}</p>}
      </div>

      {!isEditing && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Contraseña *</label>
          <input
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
            type="password"
            placeholder="Mínimo 6 caracteres"
            {...register('password')}
          />
          {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Rol *</label>
        <select
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
          {...register('role')}
        >
          <option value="">Selecciona un rol</option>
          {roleOptions.map((role) => (
            <option key={role} value={role}>
              {role === 'superadmin' ? 'Superadministrador' : role === 'admin' ? 'Administrador' : role}
            </option>
          ))}
        </select>
        {errors.role && <p className="text-sm text-destructive">{errors.role.message}</p>}
      </div>

      <RequiredFieldsLegend />
      <div className="flex items-center gap-2">
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          {isEditing ? 'Actualizar' : 'Crear'} Usuario
        </button>
        {onCancel && (
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
            onClick={onCancel}
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}
